import Link from 'next/link';
import React from 'react';

import { IPublicUserFront } from '@api/routers/user/service';
import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { Avatar, Tooltip } from '@web/ui/components';
import { getBorder } from '@web/ui/theme/utils';

const DISPLAYED_ITEMS = 5;

const HolderItems = styled.div`
  display: flex;
`;

export const getCircleStyles = (theme: Theme) => css`
  box-sizing: border-box;
  width: ${theme.size['3000']};
  height: ${theme.size['3000']};
  min-width: ${theme.size['3000']};
  min-height: ${theme.size['3000']};

  ${theme.breakpoints.up('md')} {
    width: ${theme.size['3000']};
    height: ${theme.size['3000']};
    min-width: ${theme.size['3000']};
    min-height: ${theme.size['3000']};
  }

  ${theme.breakpoints.up('lg')} {
    width: ${theme.size['3000']};
    height: ${theme.size['3000']};
    min-width: ${theme.size['3000']};
    min-height: ${theme.size['3000']};
  }
`;

const StyledLink = styled(Link)<{ zIndex: number }>`
  z-index: ${({ zIndex }) => zIndex};
  ${getBorder('1', 'onSurface', '3000')};
  transition: transform 0.15s ease;

  ${({ theme }) => getCircleStyles(theme)};

  :not(:first-child) {
    margin-left: -1.25rem;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    :not(:first-child) {
      margin-left: -1rem;
    }
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    :not(:first-child) {
      margin-left: -1.25rem;
    }
  }

  :hover {
    transform: translateY(-0.625rem);
  }

  :active {
    ${getBorder('2', 'onSurface', '3000')};
  }
`;

const ShowMoreCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.onBackground};
  width: ${({ theme }) => theme.size['3000']};
  height: ${({ theme }) => theme.size['3000']};
  min-width: ${({ theme }) => theme.size['3000']};
  min-height: ${({ theme }) => theme.size['3000']};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  margin-left: -1.25rem;

  ${getBorder('1', 'onSurface', 'full')};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: -1rem;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    margin-left: -1.25rem;
  }

  ${({ theme }) => getCircleStyles(theme)};
`;

interface NewHoldersRowProps {
  holders: IPublicUserFront[];
}

export const NewHoldersRow = ({ holders }: NewHoldersRowProps) => {
  const holdersToDisplay = holders.slice(0, DISPLAYED_ITEMS);

  return (
    <HolderItems>
      {holdersToDisplay.map((holder, index) => (
        <Tooltip content={holder.twitter.handle}>
          <StyledLink
            key={holder.id}
            href={`/profile?u=${holder.twitter.handle}`}
            zIndex={holdersToDisplay.length - index}
            onClick={(e) => e.stopPropagation()}
          >
            <Avatar
              src={holder.twitter.profilePictureURL}
              alt={`${holder.twitter.displayName} avatar`}
              borderRadius="3000"
              tokenId={holder.twitter.personaPFPTokenId}
            />
          </StyledLink>
        </Tooltip>
      ))}
      {holders.length > DISPLAYED_ITEMS && (
        <ShowMoreCircle key="show-more-circle">
          +{holders.length - DISPLAYED_ITEMS}
        </ShowMoreCircle>
      )}
    </HolderItems>
  );
};
